import { useRef, useState } from "react";
import client, { resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Save, X, Upload, Trash2, Image as ImageIcon, UserCircle2 } from "lucide-react";

const ALLOWED_MIMES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

export default function EditProfileDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gradient-to-b from-[#0a1428] to-[#050912] border-[var(--t-border-glow)] text-[var(--t-text)] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="edit-profile-dialog">
        {open && <EditProfileForm key={user.user_id} user={user} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function EditProfileForm({ user, onClose }) {
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    display_name: user.display_name || "",
    bio: user.bio || "",
    nation: user.nation || "",
    profile_picture: user.profile_picture || "",
    profile_banner: user.profile_banner || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await client.patch("/auth/me", form);
      await refresh();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save.");
    } finally {
      setBusy(false);
    }
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const inputClass = "w-full bg-[rgba(5,12,28,0.6)] border border-[var(--t-border)] focus:border-[var(--t-blue)] focus:outline-none px-3 py-2 text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)] transition";

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-xl tracking-[0.14em] uppercase text-[var(--t-cyan)]">Edit Dossier</DialogTitle>
        <DialogDescription className="font-mono text-[10px] tracking-[0.22em] text-[var(--t-text-muted)] uppercase">
          // Customise your citizen identity
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={save} className="space-y-5 mt-3">
        <ImageUploader
          label="Profile Picture"
          hint="Overrides your Discord avatar across the wiki, forum and arcade leaderboards. PNG / JPG / WebP, max 5MB."
          value={form.profile_picture}
          fallback={user.picture}
          shape="circle"
          onChange={(v) => setField("profile_picture", v)}
          onError={setError}
          testid="edit-avatar"
        />

        <ImageUploader
          label="Profile Banner"
          hint="Wide banner shown above your profile and on top of forum threads you author. PNG / JPG / WebP, max 5MB."
          value={form.profile_banner}
          fallback={null}
          shape="banner"
          onChange={(v) => setField("profile_banner", v)}
          onError={setError}
          testid="edit-banner"
        />

        <Field
          label="Display Name"
          hint={`Override your Discord name. Leave blank to use "${user.name || user.username || "your Discord name"}".`}
          testid="edit-display-name"
        >
          <input
            className={inputClass}
            maxLength={50}
            value={form.display_name}
            onChange={e => setField("display_name", e.target.value)}
            placeholder={user.name || user.username || ""}
            data-testid="edit-display-name-input"
          />
        </Field>

        <Field label="Nation" hint="The nation flag you fly. Visible on your profile." testid="edit-nation">
          <input
            className={inputClass}
            maxLength={60}
            value={form.nation}
            onChange={e => setField("nation", e.target.value)}
            placeholder="e.g. The Tropical Federation"
            data-testid="edit-nation-input"
          />
        </Field>

        <Field label="Bio" hint={`${form.bio.length}/500`} testid="edit-bio">
          <textarea
            className={inputClass}
            rows={3}
            maxLength={500}
            value={form.bio}
            onChange={e => setField("bio", e.target.value)}
            placeholder="A short blurb about your reign — strategy, history, ambitions..."
            data-testid="edit-bio-input"
          />
        </Field>

        {error && (
          <div className="text-sm text-[var(--t-red)] font-mono tracking-[0.05em]" data-testid="edit-error">{error}</div>
        )}

        <DialogFooter className="flex flex-row justify-end gap-2 pt-2 sm:gap-2">
          <button type="button" onClick={onClose} className="btn-ghost !text-xs" data-testid="edit-cancel-btn">
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button type="submit" disabled={busy} className="btn-primary !text-xs" data-testid="edit-save-btn">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </form>
    </>
  );
}

function ImageUploader({ label, hint, value, fallback, shape, onChange, onError, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // For preview we resolve relative /api/uploads/* against the API origin (vs the SPA host).
  const previewSrc = resolveImageUrl(value) || fallback || "";

  const pickFile = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!ALLOWED_MIMES.includes(f.type)) {
      onError?.(`Unsupported image type. Use PNG, JPG, or WebP.`);
      return;
    }
    if (f.size > MAX_BYTES) {
      onError?.("File too large. Max 5MB.");
      return;
    }
    onError?.("");
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const r = await client.post("/me/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const clear = () => onChange("");

  const isBanner = shape === "banner";

  return (
    <div data-testid={testid}>
      <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-2">{label}</label>
      <div className={`relative ${isBanner ? "h-28 w-full" : "w-24 h-24"} bg-[rgba(5,12,28,0.6)] border border-dashed border-[var(--t-border)] ${isBanner ? "" : "rounded-full"} overflow-hidden flex items-center justify-center group`}>
        {previewSrc ? (
          <img src={previewSrc} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
        ) : (
          <div className="text-[var(--t-text-muted)] flex flex-col items-center gap-1">
            {isBanner ? <ImageIcon className="w-5 h-5" /> : <UserCircle2 className="w-7 h-7" />}
            <span className="font-mono text-[9px] tracking-[0.18em] uppercase">{isBanner ? "No banner" : "No image"}</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-[#050912]/80 flex items-center justify-center font-mono text-[10px] tracking-[0.18em] text-[var(--t-cyan)] uppercase pulse-glow">
            // uploading
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleFile}
          data-testid={`${testid}-input`}
        />
        <button
          type="button"
          onClick={pickFile}
          disabled={uploading}
          className="btn-ghost !text-[10px] !py-1.5 !px-3"
          data-testid={`${testid}-upload-btn`}
        >
          <Upload className="w-3 h-3" /> {value ? "Replace" : "Upload"}
        </button>
        {value && (
          <button
            type="button"
            onClick={clear}
            className="btn-ghost !text-[10px] !py-1.5 !px-3 !border-[var(--t-red)]/60 !text-[var(--t-red)]"
            data-testid={`${testid}-clear-btn`}
          >
            <Trash2 className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
      {hint && <p className="font-mono text-[10px] tracking-[0.12em] text-[var(--t-text-muted)] mt-1.5">{hint}</p>}
    </div>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">{label}</label>
      {children}
      {hint && <p className="font-mono text-[10px] tracking-[0.12em] text-[var(--t-text-muted)] mt-1.5">{hint}</p>}
    </div>
  );
}
